import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, KeyRound, LogIn, Sparkles, Loader2, Inbox, RefreshCw } from "lucide-react";
import { trackPurchase } from "../lib/pixel";
import { gaPurchase } from "../lib/ga";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { AuthShell } from "./AuthPage";

const MARKET_LANG = { BR: "pt", US: "en", PT: "pt-PT", FR: "fr", ES: "es", IT: "it" };
const LANGS = ["pt", "pt-PT", "en", "es", "fr", "it"];
const POLL_MS = 6000;
const MAX_POLLS = 50;
// Universal, safe inbox links for the most common providers (no fake links otherwise).
const INBOX = { "gmail.com": "https://mail.google.com/", "googlemail.com": "https://mail.google.com/", "outlook.com": "https://outlook.live.com/mail/", "hotmail.com": "https://outlook.live.com/mail/", "live.com": "https://outlook.live.com/mail/", "msn.com": "https://outlook.live.com/mail/", "yahoo.com": "https://mail.yahoo.com/", "yahoo.com.br": "https://mail.yahoo.com/", "icloud.com": "https://www.icloud.com/mail", "me.com": "https://www.icloud.com/mail", "uol.com.br": "https://email.uol.com.br/", "bol.com.br": "https://email.bol.uol.com.br/", "terra.com.br": "https://mail.terra.com.br/" };
const track = (event, meta = {}) => api.post("/analytics/event", { event, meta }).catch(() => {});

export default function PaymentSuccess() {
  const { t, lang, setLang } = useI18n();
  const [params] = useSearchParams();
  const urlLang = (params.get("lang") || "").toLowerCase();
  const [email, setEmail] = useState((params.get("email") || "").trim().toLowerCase());
  const [draft, setDraft] = useState(email);
  const [state, setState] = useState(email ? "checking" : "need_email"); // need_email | checking | approved | timeout
  const [account, setAccount] = useState(null);
  const [resent, setResent] = useState(false);
  const polls = useRef(0);

  // Language priority: ?lang= > buyer's checkout language (from backend) > country detected by IP > saved choice.
  // A manual change in the selector on this page always wins afterwards.
  const autoLang = useRef(null);
  const userChangedLang = useRef(false);
  const applyLang = (l) => { const n = l === "pt-pt" ? "pt-PT" : l; if (LANGS.includes(n) && !userChangedLang.current) { autoLang.current = n; if (n !== lang) setLang(n); } };
  useEffect(() => { if (autoLang.current && lang !== autoLang.current) userChangedLang.current = true; }, [lang]);
  useEffect(() => {
    const l = urlLang === "pt-pt" ? "pt-PT" : urlLang.slice(0, 2);
    if (LANGS.includes(l)) { applyLang(l); return; }
    api.get("/pricing").then(({ data }) => { const m = data?.detected; if (m && MARKET_LANG[m]) applyLang(MARKET_LANG[m]); }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { track("payment_success_page_view", { lang, has_email: !!email, provider: params.get("provider") || "monetizze" }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stripe: resolve buyer e-mail from the Checkout Session (also confirms payment server-side).
  const sessionId = params.get("session_id");
  useEffect(() => {
    if (!sessionId || email) return;
    api.get(`/stripe/session/${encodeURIComponent(sessionId)}`).then(({ data }) => {
      if (data.lang) applyLang(data.lang);
      if (data.email) { setDraft(data.email); setEmail(data.email); setState("checking"); }
    }).catch(() => {});
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!email || state === "approved") return undefined;
    let stop = false;
    const check = async () => {
      try {
        const { data } = await api.post("/payments/status", { email });
        if (data.lang && !urlLang) applyLang(data.lang);
        if (stop) return;
        if (data.status === "approved") {
          setAccount(data.account); setState("approved");
          trackPurchase(data.purchase);
          gaPurchase(data.purchase, { market: data.purchase?.market, country: data.purchase?.market, language: data.lang || lang, provider: params.get("provider") || "monetizze" });
          track("payment_confirmed", { account: data.account, lang });
          track("activation_instructions_view", { account: data.account });
          return;
        }
      } catch { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(check, POLL_MS);
    };
    let timer = setTimeout(check, 0);
    return () => { stop = true; clearTimeout(timer); };
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitEmail = (e) => { e.preventDefault(); const v = draft.trim().toLowerCase(); if (!v.includes("@")) return; polls.current = 0; setEmail(v); setState("checking"); };
  const inboxUrl = INBOX[email.split("@")[1] || ""];
  const resend = async () => {
    try { await api.post("/payments/resend-activation", { email }); setResent(true); toast.success(t("ps.resent")); }
    catch (err) { toast.error(err?.response?.status === 429 ? t("ps.resend_wait") : t("ps.resent")); }
  };
  const loginCta = (
    <Link to="/login" onClick={() => track("login_cta_click", { account })} data-testid="ps-login-btn"
      className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider ${account === "existing" ? "btn-gold" : "border border-xmas-gold/40 text-xmas-snow hover:border-xmas-gold hover:text-xmas-gold"}`}>
      <LogIn className="h-4 w-4" /> {t("ps.login")}
    </Link>
  );

  return (
    <AuthShell>
      <div className="mx-auto w-full max-w-lg" data-testid="payment-success-page">
        {state !== "approved" && (
          <div className="glass rounded-3xl border border-xmas-gold/20 p-6 text-center sm:p-8" data-testid="ps-confirming">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-xmas-gold/40 bg-xmas-gold/10">
              {state === "need_email" ? <Inbox className="h-7 w-7 text-xmas-gold" /> : <Loader2 className="h-7 w-7 animate-spin text-xmas-gold" />}
            </div>
            <h1 className="mt-5 font-display text-2xl text-xmas-snow sm:text-3xl" data-testid="ps-title">{t("ps.confirming_title")}</h1>
            <p className="mt-3 text-sm text-xmas-slate">{t(state === "timeout" ? "ps.still_waiting" : "ps.confirming_text")}</p>
            {(state === "need_email" || state === "timeout") && (
              <form onSubmit={submitEmail} className="mt-6 flex flex-col gap-2 sm:flex-row" data-testid="ps-email-form">
                <input type="email" required value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("ps.email_ph")} data-testid="ps-email-input"
                  className="flex-1 rounded-full border border-xmas-gold/25 bg-xmas-navy/60 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
                <button type="submit" data-testid="ps-check-btn" className="btn-gold rounded-full px-5 py-3 text-xs font-bold uppercase tracking-wider">{t("ps.check_btn")}</button>
              </form>
            )}
            {state === "checking" && <p className="mt-4 text-xs text-xmas-slate/80" data-testid="ps-checking-email">{t("ps.checking_for")} <span className="font-mono text-xmas-snow">{email}</span></p>}
            <div className="mt-6">{loginCta}</div>
          </div>
        )}

        {state === "approved" && account === "existing" && (
          <div className="glass rounded-3xl border border-xmas-gold/20 p-6 text-center sm:p-8" data-testid="ps-existing">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
            <h1 className="mt-4 font-display text-2xl text-xmas-snow sm:text-3xl" data-testid="ps-title">{t("ps.existing_title")}</h1>
            <p className="mt-3 text-sm text-xmas-slate">{t("ps.existing_text")}</p>
            <div className="mt-6">{loginCta}</div>
          </div>
        )}

        {state === "approved" && account === "new" && (
          <div className="glass rounded-3xl border border-xmas-gold/20 p-6 sm:p-8" data-testid="ps-approved">
            <div className="text-center">
              <Sparkles className="mx-auto h-12 w-12 text-xmas-gold" />
              <h1 className="mt-3 font-display text-2xl text-xmas-snow sm:text-3xl" data-testid="ps-title">{t("ps.approved_title")}</h1>
              <p className="mt-1 text-base text-xmas-gold">{t("ps.approved_sub")}</p>
              <p className="mt-4 text-sm text-xmas-slate">{t("ps.approved_text1")}</p>
              <p className="mt-2 text-sm text-xmas-snow">{t("ps.approved_text2")}</p>
            </div>
            <ol className="mt-6 space-y-3" data-testid="ps-steps">
              {[Mail, KeyRound, Sparkles, LogIn].map((Icon, i) => (
                <li key={i} className="flex items-center gap-3 rounded-2xl border border-xmas-gold/15 bg-xmas-navy/40 px-4 py-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-xmas-gold font-mono-num text-sm font-bold text-xmas-navy">{i + 1}</span>
                  <Icon className="h-4 w-4 shrink-0 text-xmas-gold" />
                  <span className="text-sm text-xmas-snow">{t(`ps.step${i + 1}`)}</span>
                </li>
              ))}
            </ol>
            <div className="mt-5 rounded-2xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-center">
              <p className="text-sm font-semibold text-amber-200">{t("ps.not_found")}</p>
              <p className="mt-1 text-xs text-xmas-slate">{t("ps.not_found_text")}</p>
              <button onClick={resend} disabled={resent} data-testid="ps-resend-btn" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-xmas-gold underline-offset-4 hover:underline disabled:opacity-50">
                <RefreshCw className="h-3.5 w-3.5" /> {resent ? t("ps.resent") : t("ps.resend")}
              </button>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              {inboxUrl && (
                <a href={inboxUrl} target="_blank" rel="noreferrer" data-testid="ps-open-mail-btn" className="btn-gold inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider">
                  <Mail className="h-4 w-4" /> {t("ps.open_mail")}
                </a>
              )}
              {loginCta}
            </div>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
